import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import HubIcon from '@mui/icons-material/Hub';
import axios from 'axios';

import { useTranslation } from 'react-i18next';
import { usePreferencesStore } from '../../stores/preferencesStore';
import { SUPPORTED_LANGUAGES, SupportedLanguage } from '../../i18n/resources';
import { getEnvironmentTokenStorageKey } from '../../services/environmentConfig';
import { ENVIRONMENT_TOKENS_FLAG } from '../../services/featureFlags/runtimeFeatureSets';
import { useFeatureFlags } from '../../services/featureFlags/useFeatureFlags';
import NetworkFeedSelect from '../network/NetworkFeedSelect';
import { getNetworkPreference, setUserDefaultNetwork } from '../../services/network/networkPreferenceService';
import { trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
export const ProfileSettingsPanel: React.FC = () => {
  const { t } = useTranslation(['profile', 'common']);
  const environmentTokensEnabled = useFeatureFlags().isFeatureRuntimeEnabled(ENVIRONMENT_TOKENS_FLAG);
  const language = usePreferencesStore((s) => s.language);
  const setLanguage = usePreferencesStore((s) => s.setLanguage);

  // Default transit network — the user's personal override, with the org default shown
  // as the fallback they'd otherwise inherit. Stored in HTTM (see networkPreferenceService).
  const [userDefaultFeedId, setUserDefaultFeedId] = useState<string | null>(null);
  const [customerDefaultFeedId, setCustomerDefaultFeedId] = useState<string | null>(null);
  const [networkPrefLoading, setNetworkPrefLoading] = useState<boolean>(true);
  const [networkSaving, setNetworkSaving] = useState<boolean>(false);
  const [networkError, setNetworkError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getNetworkPreference()
      .then((p) => {
        if (!active) return;
        setUserDefaultFeedId(p.user_default_network_feed_id);
        setCustomerDefaultFeedId(p.customer_default_network_feed_id);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setNetworkPrefLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleDefaultNetworkChange = async (feedId: string | null) => {
    const previous = userDefaultFeedId;
    setUserDefaultFeedId(feedId); // optimistic
    setNetworkSaving(true);
    setNetworkError(null);
    try {
      const p = await setUserDefaultNetwork(feedId);
      setUserDefaultFeedId(p.user_default_network_feed_id);
      setCustomerDefaultFeedId(p.customer_default_network_feed_id);
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.default_network_changed, {
        scope: 'user',
        feed_id: feedId,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      setUserDefaultFeedId(previous); // revert on failure
      // A rejected token (HTTM 401) is the usual culprit — tell the user to re-auth
      // instead of silently snapping back to "none".
      const status = axios.isAxiosError(e) ? e.response?.status : undefined;
      setNetworkError(status === 401 ? t('profile:network.saveErrorAuth') : t('profile:network.saveError'));
    } finally {
      setNetworkSaving(false);
    }
  };

  const [uatJwtToken, setUatJwtToken] = useState<string>('');
  const [prodJwtToken, setProdJwtToken] = useState<string>('');
  const [euwe2UatJwtToken, setEuwe2UatJwtToken] = useState<string>('');
  const [euwe2ProdJwtToken, setEuwe2ProdJwtToken] = useState<string>('');
  const [showUatToken, setShowUatToken] = useState<boolean>(false);
  const [showProdToken, setShowProdToken] = useState<boolean>(false);
  const [showEuwe2UatToken, setShowEuwe2UatToken] = useState<boolean>(false);
  const [showEuwe2ProdToken, setShowEuwe2ProdToken] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  useEffect(() => {
    const savedUatToken = localStorage.getItem('atlas_uatJwtToken') || '';
    const savedProdToken = localStorage.getItem('atlas_prodJwtToken') || '';
    const savedEuwe2UatToken = localStorage.getItem(getEnvironmentTokenStorageKey('EUWE2_UAT')) || '';
    const savedEuwe2ProdToken = localStorage.getItem(getEnvironmentTokenStorageKey('EUWE2_Production')) || '';
    setUatJwtToken(savedUatToken);
    setProdJwtToken(savedProdToken);
    setEuwe2UatJwtToken(savedEuwe2UatToken);
    setEuwe2ProdJwtToken(savedEuwe2ProdToken);
    setHasChanges(false);
  }, []);

  useEffect(() => {
    const savedUatToken = localStorage.getItem('atlas_uatJwtToken') || '';
    const savedProdToken = localStorage.getItem('atlas_prodJwtToken') || '';
    const savedEuwe2UatToken = localStorage.getItem(getEnvironmentTokenStorageKey('EUWE2_UAT')) || '';
    const savedEuwe2ProdToken = localStorage.getItem(getEnvironmentTokenStorageKey('EUWE2_Production')) || '';
    const changed =
      uatJwtToken !== savedUatToken ||
      prodJwtToken !== savedProdToken ||
      euwe2UatJwtToken !== savedEuwe2UatToken ||
      euwe2ProdJwtToken !== savedEuwe2ProdToken;
    setHasChanges(changed);
  }, [uatJwtToken, prodJwtToken, euwe2UatJwtToken, euwe2ProdJwtToken]);

  const handleSave = () => {
    if (uatJwtToken.trim()) {
      localStorage.setItem('atlas_uatJwtToken', uatJwtToken.trim());
    } else {
      localStorage.removeItem('atlas_uatJwtToken');
    }

    if (prodJwtToken.trim()) {
      localStorage.setItem('atlas_prodJwtToken', prodJwtToken.trim());
    } else {
      localStorage.removeItem('atlas_prodJwtToken');
    }
    if (euwe2UatJwtToken.trim()) {
      localStorage.setItem(getEnvironmentTokenStorageKey('EUWE2_UAT'), euwe2UatJwtToken.trim());
    } else {
      localStorage.removeItem(getEnvironmentTokenStorageKey('EUWE2_UAT'));
    }
    if (euwe2ProdJwtToken.trim()) {
      localStorage.setItem(getEnvironmentTokenStorageKey('EUWE2_Production'), euwe2ProdJwtToken.trim());
    } else {
      localStorage.removeItem(getEnvironmentTokenStorageKey('EUWE2_Production'));
    }

    setHasChanges(false);
  };

  const isUatTokenValid = uatJwtToken.trim().length === 0 || uatJwtToken.trim().length >= 10;
  const isProdTokenValid = prodJwtToken.trim().length === 0 || prodJwtToken.trim().length >= 10;
  const isEuwe2UatTokenValid = euwe2UatJwtToken.trim().length === 0 || euwe2UatJwtToken.trim().length >= 10;
  const isEuwe2ProdTokenValid = euwe2ProdJwtToken.trim().length === 0 || euwe2ProdJwtToken.trim().length >= 10;

  const languageLabelMap: Record<SupportedLanguage, string> = {
    en: t('common:language.english'),
    'pt-BR': t('common:language.portugueseBR'),
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        {t('profile:panel.title')}
      </Typography>

      <Box>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
        >
          <Icon d={ICON_FALLBACKS.Language} size={20} style={{ color: '#ef7335' }} />
          {t('common:language.label')}
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel id="profile-language-selector-label">
            {t('common:language.label')}
          </InputLabel>
          <Select
            labelId="profile-language-selector-label"
            value={language}
            label={t('common:language.label')}
            onChange={(e) => setLanguage(e.target.value as SupportedLanguage)}
          >
            {SUPPORTED_LANGUAGES.map((lng) => (
              <MenuItem key={lng} value={lng}>
                {languageLabelMap[lng]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box>
        <Typography
          variant="subtitle2"
          fontWeight={600}
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
        >
          <HubIcon fontSize="small" sx={{ color: '#ef7335' }} />
          {t('profile:network.title')}
        </Typography>
        <NetworkFeedSelect
          label={t('profile:network.selectLabel')}
          value={userDefaultFeedId}
          onChange={handleDefaultNetworkChange}
          disabled={networkPrefLoading || networkSaving}
          noneLabel={customerDefaultFeedId ? t('profile:network.useOrgDefault') : t('profile:network.none')}
          helperText={
            userDefaultFeedId == null && customerDefaultFeedId
              ? t('profile:network.inheriting')
              : t('profile:network.help')
          }
        />
        {networkError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            {networkError}
          </Typography>
        )}
      </Box>

      {environmentTokensEnabled && (
        <>
          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              EUWE2 UAT JWT Token
            </Typography>
            <TextField
              fullWidth
              type={showEuwe2UatToken ? 'text' : 'password'}
              label="EUWE2 UAT JWT Token"
              value={euwe2UatJwtToken}
              onChange={(e) => setEuwe2UatJwtToken(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Toggle EUWE2 UAT token visibility"
                      onClick={() => setShowEuwe2UatToken(!showEuwe2UatToken)}
                      edge="end"
                      size="small"
                    >
                      {showEuwe2UatToken ? <Icon size={20} name="eye-slash" /> : <Icon size={20} name="eye" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            {euwe2UatJwtToken.trim() ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isEuwe2UatTokenValid ? (
                  <>
                    <Icon size={16} name="circle-check" style={{ color: '#2e7d32'}} />
                    <Typography variant="caption" color="success.main">
                      {t('profile:settings.tokenValid')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Icon size={16} name="circle-exclamation" style={{ color: '#ed6c02'}} />
                    <Typography variant="caption" color="warning.main">
                      {t('profile:settings.tokenInvalid')}
                    </Typography>
                  </>
                )}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('profile:settings.tokenEmpty')}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              EUWE2 Prod JWT Token
            </Typography>
            <TextField
              fullWidth
              type={showEuwe2ProdToken ? 'text' : 'password'}
              label="EUWE2 Prod JWT Token"
              value={euwe2ProdJwtToken}
              onChange={(e) => setEuwe2ProdJwtToken(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Toggle EUWE2 Prod token visibility"
                      onClick={() => setShowEuwe2ProdToken(!showEuwe2ProdToken)}
                      edge="end"
                      size="small"
                    >
                      {showEuwe2ProdToken ? <Icon size={20} name="eye-slash" /> : <Icon size={20} name="eye" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            {euwe2ProdJwtToken.trim() ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isEuwe2ProdTokenValid ? (
                  <>
                    <Icon size={16} name="circle-check" style={{ color: '#2e7d32'}} />
                    <Typography variant="caption" color="success.main">
                      {t('profile:settings.tokenValid')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Icon size={16} name="circle-exclamation" style={{ color: '#ed6c02'}} />
                    <Typography variant="caption" color="warning.main">
                      {t('profile:settings.tokenInvalid')}
                    </Typography>
                  </>
                )}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('profile:settings.tokenEmpty')}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('profile:settings.uatTokenLabel')}
            </Typography>
            <TextField
              fullWidth
              type={showUatToken ? 'text' : 'password'}
              label={t('profile:settings.uatTokenLabel')}
              value={uatJwtToken}
              onChange={(e) => setUatJwtToken(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t('profile:settings.toggleUatVisibility')}
                      onClick={() => setShowUatToken(!showUatToken)}
                      edge="end"
                      size="small"
                    >
                      {showUatToken ? <Icon size={20} name="eye-slash" /> : <Icon size={20} name="eye" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            {uatJwtToken.trim() ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isUatTokenValid ? (
                  <>
                    <Icon size={16} name="circle-check" style={{ color: '#2e7d32'}} />
                    <Typography variant="caption" color="success.main">
                      {t('profile:settings.tokenValid')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Icon size={16} name="circle-exclamation" style={{ color: '#ed6c02'}} />
                    <Typography variant="caption" color="warning.main">
                      {t('profile:settings.tokenInvalid')}
                    </Typography>
                  </>
                )}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('profile:settings.tokenEmpty')}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('profile:settings.prodTokenLabel')}
            </Typography>
            <TextField
              fullWidth
              type={showProdToken ? 'text' : 'password'}
              label={t('profile:settings.prodTokenLabel')}
              value={prodJwtToken}
              onChange={(e) => setProdJwtToken(e.target.value)}
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={t('profile:settings.toggleProdVisibility')}
                      onClick={() => setShowProdToken(!showProdToken)}
                      edge="end"
                      size="small"
                    >
                      {showProdToken ? <Icon size={20} name="eye-slash" /> : <Icon size={20} name="eye" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            {prodJwtToken.trim() ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isProdTokenValid ? (
                  <>
                    <Icon size={16} name="circle-check" style={{ color: '#2e7d32'}} />
                    <Typography variant="caption" color="success.main">
                      {t('profile:settings.tokenValid')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Icon size={16} name="circle-exclamation" style={{ color: '#ed6c02'}} />
                    <Typography variant="caption" color="warning.main">
                      {t('profile:settings.tokenInvalid')}
                    </Typography>
                  </>
                )}
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {t('profile:settings.tokenEmpty')}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!hasChanges || !isUatTokenValid || !isProdTokenValid}
          >
            {t('profile:panel.saveButton')}
          </Button>
        </>
      )}
    </Box>
  );
};
